import { Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Database, RefreshCw } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { DetailFact, DetailGrid, MetadataTable, truncateMiddle } from "@/components/common/AdminDetails";
import { ErrorState, LoadingRow } from "@/components/common/StateViews";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { knowledgeBaseApi, tenantApi, type Tenant } from "@/lib/proxy-api";
import { ApiException } from "@/lib/api-client";
import { useTenantWorkspace } from "@/lib/tenant-workspace";

export function KnowledgeBasesPage() {
  const workspace = useTenantWorkspace();
  const tenantId = workspace.tenantId;

  const tenantQ = useQuery({
    queryKey: ["tenant", tenantId],
    queryFn: () => tenantApi.get(tenantId!),
    enabled: Boolean(tenantId),
  });
  const verifyQ = useQuery({
    queryKey: ["knowledge-base-verify", tenantId],
    queryFn: () => knowledgeBaseApi.verify(tenantId!),
    enabled: workspace.role === "super_admin" && Boolean(tenantId),
  });

  const tenant = tenantQ.data as Tenant | undefined;
  const canVerify = workspace.role === "super_admin";

  if (!tenantId) return <Navigate to="/tenants" replace />;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Knowledge Base"
        description={tenant ? `Bedrock Knowledge Base for ${tenant.name}` : "Bedrock Knowledge Base status and verification."}
        actions={
          canVerify ? (
            <Button variant="outline" onClick={() => verifyQ.refetch()} disabled={verifyQ.isFetching}>
              <RefreshCw className="h-4 w-4" />
              Verify
            </Button>
          ) : undefined
        }
      />

      {tenantQ.isLoading ? (
        <LoadingRow cols={3} />
      ) : tenantQ.error ? (
        <ErrorState message={tenantQ.error instanceof ApiException ? tenantQ.error.message : "Failed to load tenant"} />
      ) : (
        <Card>
          <CardContent className="space-y-4">
            <DetailGrid>
              <DetailFact label="Tenant Status" value={tenant?.status ?? "unknown"} />
              <DetailFact label="Knowledge Base" value={tenant?.knowledge_base_id ?? "not configured"} />
              <DetailFact label="Knowledge Base Status" value={tenant?.knowledge_base_status ?? "not exposed"} />
              <DetailFact label="Data Source" value={tenant?.data_source_id ?? "not configured"} />
              <DetailFact label="Documents Bucket" value={tenant?.documents_bucket ?? "not configured"} />
              <DetailFact label="Vector Bucket" value={tenant?.vector_bucket_name ?? "not configured"} />
              <DetailFact label="Vector Index" value={tenant?.vector_index_name ?? "not configured"} />
              <DetailFact label="Vector Index ARN" value={truncateMiddle(tenant?.vector_index_arn, 24)} />
              <DetailFact label="Embedding Model" value={tenant?.effective_defaults?.embedding_model ?? truncateMiddle(tenant?.embedding_model_arn, 24)} className="lg:col-span-2" />
            </DetailGrid>
          </CardContent>
        </Card>
      )}

      {canVerify ? (
        <Card>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-2">
              <Database className="h-5 w-5 text-primary" />
              <h2 className="font-semibold">Resource verification</h2>
              {verifyQ.isFetching && <Badge variant="outline">checking</Badge>}
            </div>
            {verifyQ.error ? (
              <ErrorState message={verifyQ.error instanceof ApiException ? verifyQ.error.message : "Verification failed"} />
            ) : (
              <VerificationSummary data={verifyQ.data} />
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="text-sm text-muted-foreground">
            Knowledge Base verification currently requires Superuser access.
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function VerificationSummary({ data }: { data: unknown }) {
  if (!data) {
    return <p className="text-sm text-muted-foreground">Run verification to inspect tenant resources.</p>;
  }

  const rows = summarizeVerification(data);
  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {rows.map((row) => (
          <DetailFact key={row.label} label={row.label} value={row.value} />
        ))}
      </div>
      {isRecord(data) ? (
        <MetadataTable value={data} emptyLabel="Verification completed but did not return resource details." />
      ) : null}
    </div>
  );
}

function summarizeVerification(data: unknown): Array<{ label: string; value: string }> {
  if (!data || typeof data !== "object") return [{ label: "Result", value: String(data) }];
  return Object.entries(data as Record<string, unknown>).slice(0, 9).map(([key, value]) => ({
    label: labelize(key),
    value: summarizeValue(value),
  }));
}

function summarizeValue(value: unknown): string {
  if (Array.isArray(value)) return `${value.length} item${value.length === 1 ? "" : "s"}`;
  if (value === null || value === undefined) return "none";
  if (typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>);
    if (entries.length === 0) return "empty";
    return entries.slice(0, 3).map(([key, nested]) => `${labelize(key)}: ${summarizeValue(nested)}`).join(" · ");
  }
  return String(value);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function labelize(key: string): string {
  return key.replace(/[_-]/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
}
